import React, { useContext, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EventIcon from '@mui/icons-material/Event'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import API, { runGlobalApiErrorHandler } from '../../../../services/API'
import moment from 'moment'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { GlobalContext } from '../../../../App'
import ConcurrentRoomLimitAlert from '../../../../components/Subscriptions/ConcurrentRoomLimitAlert'
import { isControlRoomLimit } from '../../../../helpers/controlRoomCapacity'

const InfoRow = ({ label, value, icon }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 3,
      backgroundColor: '#F8FAFC',
      border: '1px solid #E2E8F0',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icon && (
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#E6F7F9',
            color: '#1E7F88',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}

      <Stack spacing={0.2}>
        <Typography variant="caption" color="text.secondary" fontWeight={800}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={800} color="#242F40">
          {value || '—'}
        </Typography>
      </Stack>
    </Stack>
  </Box>
)

const InfoPopup = ({
  open,
  onClose,
  linkedEvent,
  data,
  controlRoomInfo,
  refreshControlRoom,
  // Opening/closing a control room is a commander decision — everyone else
  // just sees the room info.
  isCommander = true,
}) => {
  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const { setToast } = useContext(GlobalContext)

  const [updating, setUpdating] = useState(false)
  const [confirmingClose, setConfirmingClose] = useState(false)
  // Reopening restarts the room's clock (its time is recorded in the
  // organisation's control-room hours) and takes one of the plan's
  // concurrent control-room places, so it is confirmed, like closing.
  const [confirmingReopen, setConfirmingReopen] = useState(false)
  // The 403 payload when the concurrent control-room capacity is in use.
  const [reopenLimit, setReopenLimit] = useState(null)

  const isClosed = data?.status === 'Closed'
  const hasActiveIncidents = !!data?.activeIncidents?.length
  const resourceCount = data?.resources?.length ?? 0
  const crewedCount = (data?.resources ?? []).filter(
    (r) => r?.users?.length,
  ).length

  const handleDismiss = () => {
    setConfirmingClose(false)
    setConfirmingReopen(false)
    setReopenLimit(null)
    onClose()
  }

  const handleCloseControlRoom = async () => {
    setUpdating(true)

    try {
      const res = await API.post(`/controlroom/${controlRoomInfo}/close`)

      if (res.status === 200) {
        setSiteMode('normal')
        setSelectedControlRoom(null)
        setToast({ message: 'Control Room Closed' })
        handleDismiss()
      } else {
        setToast({
          message:
            res.data?.error ||
            'Unable to close this control room while treatment locations still have patients.',
          severity: 'error',
        })
      }
    } finally {
      setUpdating(false)
    }
  }

  const handleOpenControlRoom = async () => {
    setUpdating(true)
    setReopenLimit(null)

    try {
      // skipGlobalErrorHandler so the concurrent-room refusal is explained
      // inline; any other failure still goes to the global handler first, as
      // before.
      const res = await API.post(
        `/controlroom/${controlRoomInfo}/reopen`,
        undefined,
        { skipGlobalErrorHandler: true },
      )

      if (res.status === 200) {
        setConfirmingReopen(false)
        refreshControlRoom()
        setToast({ message: 'Control Room Reopened' })
        setTimeout(() => onClose(), 300)
      } else if (isControlRoomLimit(res)) {
        setReopenLimit(res.data)
      } else if (!runGlobalApiErrorHandler({ response: res })) {
        setToast({
          message: 'Unable to Reopen Control Room',
          severity: 'error',
        })
      }
    } catch (e) {
      if (!runGlobalApiErrorHandler(e)) {
        setToast({
          message: 'Unable to Reopen Control Room',
          severity: 'error',
        })
      }
    } finally {
      setUpdating(false)
    }
  }

  return (
    <BigModal
      open={open}
      onClose={handleDismiss}
      autoWidth
      style={{ width: { xs: '94vw', sm: 'auto' }, maxWidth: { xs: '94vw', sm: '90vw' } }}
    >
      <Box sx={{ width: '100%', maxWidth: 580 }}>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 3,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Stack spacing={0.25}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Command & Control
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                Control Room Info
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.78, color: '#fff' }}>
                View room status and operational controls.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={data?.status || 'Unknown'}
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                backgroundColor: isClosed ? '#E2E8F0' : '#DCFCE7',
                color: isClosed ? '#334155' : '#166534',
              }}
            />

            {data?.controlRoomRef && (
              <Chip
                label={`Room ${data.controlRoomRef}`}
                sx={{
                  borderRadius: 999,
                  fontWeight: 900,
                  backgroundColor: '#E6F7F9',
                  color: '#1E7F88',
                }}
              />
            )}
          </Stack>

          <Stack spacing={1.5}>
            <InfoRow
              label="Status"
              value={data?.status}
              icon={isClosed ? <LockIcon /> : <LockOpenIcon />}
            />

            <InfoRow
              label="Started"
              value={
                data?.started
                  ? moment(data.started).format('DD/MM/YYYY HH:mm')
                  : '—'
              }
              icon={<AccessTimeIcon />}
            />

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#E6F7F9',
                        color: '#1E7F88',
                        flexShrink: 0,
                      }}
                    >
                      <EventIcon />
                    </Box>

                    <Stack spacing={0.2}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={800}
                      >
                        Linked Event
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        color="#242F40"
                      >
                        {linkedEvent?.title ? linkedEvent.title : 'This control room is not linked to an event'}
                      </Typography>
                    </Stack>
                  </Stack>

                  {linkedEvent && <Button
                    onClick={() =>
                      window.open(`/events/${linkedEvent._id}`, '_blank')
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 800,
                      borderColor: '#239AA4',
                      color: '#1E7F88',
                    }}
                  >
                    View Event
                  </Button>}
                </Stack>
              </Box>
          </Stack>

          <Divider />

          {!isCommander ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
                border: '1px solid rgba(35,154,164,0.24)',
              }}
            >
              Only a commander can {isClosed ? 'reopen' : 'close'} this control
              room.
            </Alert>
          ) : isClosed && !confirmingReopen ? (
            <Button
              onClick={() => setConfirmingReopen(true)}
              disabled={updating}
              variant="contained"
              fullWidth
              endIcon={
                updating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LockOpenIcon />
                )
              }
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#239AA4',
                boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
                '&:hover': {
                  backgroundColor: '#1E7F88',
                },
              }}
            >
              Reopen Control Room
            </Button>
          ) : isClosed ? (
            <Stack spacing={1.5}>
              <Alert
                severity="info"
                sx={{
                  borderRadius: 3,
                  backgroundColor: '#E6F7F9',
                  color: '#1E7F88',
                  border: '1px solid rgba(35,154,164,0.24)',
                }}
              >
                Reopening makes this control room live again: crews can be
                assigned, it takes one of your plan's concurrently open
                control-room places, and its time is recorded in your
                control-room hours until it is closed.
              </Alert>
              {reopenLimit && (
                <ConcurrentRoomLimitAlert limitInfo={reopenLimit} />
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={() => {
                    setConfirmingReopen(false)
                    setReopenLimit(null)
                  }}
                  disabled={updating}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleOpenControlRoom}
                  disabled={updating}
                  variant="contained"
                  fullWidth
                  endIcon={
                    updating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LockOpenIcon />
                    )
                  }
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 900,
                    backgroundColor: '#239AA4',
                    '&:hover': { backgroundColor: '#1E7F88' },
                  }}
                >
                  Confirm reopen
                </Button>
              </Stack>
            </Stack>
          ) : hasActiveIncidents ? (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 3,
                backgroundColor: '#FFF7ED',
                color: '#9A3412',
                border: '1px solid #FED7AA',
              }}
            >
              You cannot close this control room while there are active
              incidents.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {confirmingClose && resourceCount > 0 && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 3,
                    backgroundColor: '#FFF7ED',
                    color: '#9A3412',
                    border: '1px solid #FED7AA',
                  }}
                >
                  <Typography variant="body2" fontWeight={800} gutterBottom>
                    Closing this control room will:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>
                      Remove all assigned staff from every resource
                      {crewedCount > 0 &&
                        ` (${crewedCount} of ${resourceCount} resource${resourceCount === 1 ? '' : 's'} currently ${crewedCount === 1 ? 'has' : 'have'} staff assigned)`}
                    </li>
                    <li>
                      Set all {resourceCount} resource
                      {resourceCount === 1 ? '' : 's'} to Off Duty and release
                      them from this control room
                    </li>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Reopening later restores any resources not claimed by
                    another control room — staff will need to be re-assigned.
                  </Typography>
                </Alert>
              )}

              <Button
                onClick={
                  confirmingClose || resourceCount === 0
                    ? handleCloseControlRoom
                    : () => setConfirmingClose(true)
                }
                disabled={updating}
                variant="contained"
                fullWidth
                endIcon={
                  updating ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <LockIcon />
                  )
                }
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#E32525',
                  boxShadow: '0 12px 28px rgba(227,37,37,0.24)',
                  '&:hover': {
                    backgroundColor: '#B91C1C',
                  },
                }}
              >
                {confirmingClose
                  ? 'Confirm — Close Control Room'
                  : 'Close Control Room'}
              </Button>

              {confirmingClose && (
                <Button
                  onClick={() => setConfirmingClose(false)}
                  disabled={updating}
                  fullWidth
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 800,
                    color: '#64748B',
                  }}
                >
                  Keep Control Room Open
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </BigModal>
  )
}

export default InfoPopup
